// Zod schema for the promotion wizard (PR-1..PR-4). Shared between form
// validation and the assembled PromotionInput the create/update mutations send.

import { z } from "zod";

const channelSchema = z.object({
  enabled: z.boolean(),
  promo_code: z.string().trim().nullable().optional().transform((v) => (v ? v : null)),
  target_url: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || /^https?:\/\//i.test(v), {
      message: "Ordering link must start with http:// or https://",
    }),
});

export const promotionSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, "Title is required")
      .max(60, "Keep the title under 60 characters"),
    description: z
      .string()
      .trim()
      .max(200, "Keep the description under 200 characters")
      .nullable()
      .optional()
      .transform((v) => (v ? v : null)),
    terms: z.string().trim().min(1, "Terms are required"),
    redemption_instructions: z.string().trim().min(1, "Redemption instructions are required"),
    estimated_value: z
      .number({ invalid_type_error: "Enter an estimated value" })
      .min(0, "Estimated value must be zero or more"),
    image_url: z.string().trim().nullable().optional().transform((v) => (v ? v : null)),
    default_expiration_days: z
      .number()
      .int()
      .min(1)
      .max(365),
    default_timer_minutes: z
      .number()
      .int()
      .min(5)
      .max(1440),
    channels: z.object({
      phone: channelSchema,
      in_store: channelSchema,
      online: channelSchema,
    }),
  })
  .superRefine((data, ctx) => {
    const anyEnabled = Object.values(data.channels).some((c) => c.enabled);
    if (!anyEnabled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["channels"],
        message: "Turn on at least one redemption method",
      });
    }
  });

export type PromotionFormValues = z.infer<typeof promotionSchema>;
